import {ChangeEvent, KeyboardEvent, useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword, resetPassword, verifyResetOtp} from '../core/_requests'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {toast as message} from 'react-toastify'

type ForgotPasswordStep = 'email' | 'otp' | 'reset' | 'success'

const initialValues = {
  email: '',
  otp: '',
  password: '',
  confirmPassword: '',
}

const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

const otpSchema = Yup.object().shape({
  otp: Yup.string().length(4, 'Enter 4 digit OTP').required('OTP is required'),
})

const resetPasswordSchema = Yup.object().shape({
   password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long.')
    .max(50, 'Maximum 50 characters.')
    .matches(
      /[A-Za-z]/,
      'Password must contain at least one alphabetic character.'
    )
    .matches(
      /\d/,
      'Password must contain at least one number.'
    ),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Password and confirm password must match'),
})

export function ForgotPassword() {
  const [step, setStep] = useState<ForgotPasswordStep>('email')
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([])
  const navigate = useNavigate()

  const getValidationSchema = () => {
    if (step === 'otp') {
      return otpSchema
    }

    if (step === 'reset') {
      return resetPasswordSchema
    }

    return emailSchema
  }

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setStatus(undefined)

      try {
        if (step === 'email') {
          const response = await requestPassword({
            username: values.email,
          });
        
          if (response.data.success) {
            message.success(response.data.message);
            setStep('otp');
          } else {
            message.error(response.data.data);
          }
        }

        if (step === 'otp') {
          const response = await verifyResetOtp({
            mail: values.email,
            otp: values.otp,
            filetype: 'email',
          });
        
          if (response.data.success) {
            message.success(response.data.message);
            setStep('reset');
          } else {
            message.error(response.data.message);
          }
        }

        if (step === 'reset') {
          if (values.password !== values.confirmPassword) {
            setHasErrors(true)
            setStatus('Password and Confirm Password do not match.')
            return
          }
          const response = await resetPassword({
            userEmail: values.email,
            password: values.password,
          });
        
          if (response.data.success) {
            message.success(response.data.message);
            navigate('/auth/login', { replace: true });
          } else {
            message.error(response.data.message);
          }
        }
        

        
      // } catch (error) {
      //   setHasErrors(true)
      //   setStatus('Unable to complete the request. Please try again.')
      // } 
      }catch (error: any) {
        message.error(
          error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again."
        );
      }
      finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  const title =
    step === 'otp'
      ? 'Enter OTP'
      : step === 'reset'
      ? 'Reset Password'
      : step === 'success'
      ? 'Password Reset'
      : 'Forgot Password ?'

  const subtitle =
    step === 'otp'
      ? 'Enter the OTP sent to your email.'
      : step === 'reset'
      ? 'Create a new password for your account.'
      : step === 'success'
      ? 'Your password has been reset successfully.'
      : 'Enter your email to reset your password.'

  const handleOtpChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = event.target.value.replace(/\D/g, '').slice(-1)
    const otp = formik.values.otp.split('')
    otp[index] = value

    formik.setFieldValue('otp', otp.join('').slice(0, 4))

    if (value && index < 3) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !formik.values.otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <img
          className='mx-auto w-275px'
          src={toAbsoluteUrl('/media/logos/healon/logo-2.png')}
          alt=''
        />

        <h1 className='fs-2x fw-bolder mb-8' style={{color: '#009D84'}}>
          COMS
        </h1>
        <h2 className='text-dark fw-bolder mb-3'>{title}</h2>
        <div className='text-gray-500 fw-semibold fs-6'>{subtitle}</div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {formik.status || 'Sorry, looks like there are some errors detected, please try again.'}
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>Password reset successful. Please login with your new password.</div>
        </div>
      )}

      {step === 'email' && (
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6'>Email</label>
          <input
            type='email'
            placeholder='Email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {step === "otp"&&(
       <div className='fv-row mb-8'>
       <div className='d-flex gap-3 justify-content-center'>
         {[0, 1, 2, 3].map((index) => (
           <input
             key={index}
             ref={(element) => {
               otpInputRefs.current[index] = element
             }}
             type='text'
             inputMode='numeric'
             autoComplete={index === 0 ? 'one-time-code' : 'off'}
             value={formik.values.otp[index] || ''}
             onBlur={() => formik.setFieldTouched('otp', true)}
             onChange={(event) => handleOtpChange(event, index)}
             onKeyDown={(event) => handleOtpKeyDown(event, index)}
             className='form-control bg-transparent text-center fw-bolder fs-2 h-60px w-60px text-dark'
             maxLength={1}
           />
         ))}
       </div>
     
       {formik.touched.otp && formik.errors.otp ? (
         <div className='fv-plugins-message-container mt-2'>
           <div className='fv-help-block text-danger'>
             <span>{formik.errors.otp}</span>
           </div>
         </div>
       ) : null}
     </div>
      )}

      { step === 'reset' &&(
        <>
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6'>Password</label>
      
          <div className='position-relative'>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder='Password'
              autoComplete='new-password'
              {...formik.getFieldProps('password')}
              className='form-control bg-transparent pe-10'
            />
      
            <span
              onClick={() => setShowPassword(!showPassword)}
              className='position-absolute top-50 end-0 translate-middle-y me-3'
              style={{cursor: 'pointer'}}
            >
              <i className={showPassword ? 'bi bi-eye fs-3' : 'bi bi-eye-slash fs-3'}></i>
            </span>
          </div>
      
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container mt-2'>
              <div className='fv-help-block text-danger'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
      
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6'>
            Confirm Password
          </label>
      
          <div className='position-relative'>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder='Confirm Password'
              autoComplete='new-password'
              {...formik.getFieldProps('confirmPassword')}
              className='form-control bg-transparent pe-10'
            />
      
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className='position-absolute top-50 end-0 translate-middle-y me-3'
              style={{cursor: 'pointer'}}
            >
              <i
                className={
                  showConfirmPassword
                    ? 'bi bi-eye fs-3'
                    : 'bi bi-eye-slash fs-3'
                }
              ></i>
            </span>
          </div>
      
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className='fv-plugins-message-container mt-2'>
              <div className='fv-help-block text-danger'>
                <span role='alert'>{formik.errors.confirmPassword}</span>
              </div>
            </div>
          )}
        </div>
      </>
      )}

      <div className='text-center'>
        {step !== 'success' && (
          <button
            type='submit'
            id='kt_password_reset_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || loading}
          >
            {!loading && (
              <span className='indicator-label'>
                {step === 'email' ? 'Submit' : step === 'otp' ? 'Verify OTP' : 'Reset Password'}
              </span>
            )}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        )}

        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-lg btn-light-primary w-100 mb-5'
            disabled={loading}
          >
            {step === 'success' ? 'Back to Login' : 'Cancel'}
          </button>
        </Link>
      </div>
    </form>
  )
}
